import * as React from 'react';
import { ClockCircleOutlined, EnvironmentOutlined, PhoneOutlined } from '@ant-design/icons';
import Ads from '_components/Ads';
import AdsOutOfPage from '_components/AdsOutOfPage';
import BreadCrumb from '_components/BreadCrumb';
import COTY2024WinnerCard from '_components/COTY2024/winnerCard';
import DealerRelatedMakes from '_components/DealerRelatedMakes';
import GetQuoteCard from '_components/GetQuoteCard';
import ArticleCard from '_components/article-v3/ArticleCard';
import Layout from '_components/layout';
import SEO from '_components/seo_v3';
import { createArticleListData, createBreadcrumbData, createOrganizationData, createAutoDealerData, createAutoRepairData } from '_utils/structuredData';
import fbIcon from '_images/fb-icon.svg';
import igIcon from '_images/ig-icon.svg';
import linkedinIcon from '_images/linkedin-icon.svg';
import rightArrow from '_images/right-arrow.svg';
import tiktokIcon from '_images/tiktok-icon.svg';
import websiteIcon from '_images/website-icon.svg';
import youtubeIcon from '_images/youtube-icon.svg';
import { slugForMakeProfile } from '_utils/cars';
import { urlFor } from '_utils/images';
import { Button, Collapse, Select, Tabs } from 'antd';
import { graphql, navigate } from 'gatsby';
import { orderBy, propertyOf } from 'lodash';
import moment from 'moment';
import './make-profile.less';

const MakeProfile = ({data}) => {
  const make = data.sanityMake;
  const slug = slugForMakeProfile(make);

  const reviews  = data.reviews?.edges.map((d) => {
    return d.node;
  });

  const articles  = data.articles?.edges.map((d) => {
    return d.node;
  });

  const breadCrumbItems = [
    { label: 'Home', url: '/' },
    { label: 'Buy', url: '/' },
    { label: 'New Car Pricelists', url: '/new-cars/price-lists' },
    { label: make.name, current: true },
  ];

  let selectedPriceList = null;
  
  const getOpeningHoursString = (hours) => {
    if (hours) {
      const hoursArr = hours.split("-");
      const startTime = moment(hoursArr[0],"HHmm").format("hh:mma");
      const endTime = moment(hoursArr[1],"HHmm").format("hh:mma");
      return startTime + " - " + endTime;
    }
    else {
      return null;
    }
  }

  const getOpeningHoursDay = (key) => {
    let day;
    if (key === 'ph') {
      day = "Public Holiday";
    }
    else {
      day = moment(key, 'ddd').format('dddd');
    }
    return day;
  }

  const getOpeningHoursLine = (key, value) => {
    const day = getOpeningHoursDay(key);
    let time = getOpeningHoursString(value);
    if (!time) {
      time = "Closed";
    }
    return `${day}: ${time}`;
  }

  const OpeningHours = ({openingHours}) => {
    const today = moment().format('ddd').toLowerCase();
    
    if (openingHours) {
      const hours = openingHours[today];
      const header = getOpeningHoursLine(today, hours);

      const days = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun', 'ph'];

      return (
        <div className="operating-hours">
          <Collapse ghost defaultActiveKey="1" expandIconPosition="right">
            <Panel header={header}>
            {days.map((day,i) => {
              if (openingHours[day]) {
              return (
              <div className="flex flex-row gap-x-1" key={i}>
                <div className="w-[75px]">{getOpeningHoursDay(day)}</div>
                <div>:</div>
                <div>{getOpeningHoursString(openingHours[day])}</div>
              </div>
              )}})}
            </Panel>
          </Collapse>
        </div>
      )
    }
    return null;
  }

  const { Panel } = Collapse;
  const PanelHead = ({dealer, priceLists}) => {
    // Filter out any invalid dates first
    const validPriceLists = priceLists.filter(pl => pl.datePublished && moment(pl.datePublished).isValid());
    
    // Sort by converting dates to timestamps for reliable comparison
    const sortedPriceLists = orderBy(validPriceLists, (pl) => {
      const date = moment(pl.datePublished);
      return date.valueOf();
    }, 'desc');
    
    const priceListOptions = [];
    sortedPriceLists.map((pl,i) => {
      if (pl.file.asset) {
        priceListOptions.push({label: moment(pl.datePublished).format("DD MMM YYYY"), value: pl.file.asset.url});
      }
    })
    
    if (priceListOptions.length > 0) {
      selectedPriceList = priceListOptions[0].value;
    }

    const selectPriceList = (value) => {
      selectedPriceList = value;
    }

    const downloadPriceList = () => {
      window.open(selectedPriceList, "_blank");
    }
    
    const socialIcons = {
      instagram: igIcon,
      facebook: fbIcon,
      tiktok: tiktokIcon,
      youtube: youtubeIcon,
      linkedin: linkedinIcon,
    }

    return (
    <div className="w-full flex xl:flex-row lg:flex-col md:flex-row flex-col xl:justify-between lg:justify-start md:justify-between justify-start xl:items-center lg:items-start md:items-center items-start xl:p-4 p-2 gap-y-4">
      <div className="flex sm:flex-row flex-col sm:items-center items-start sm:divide-x divide-x-0 divide-gray-300 gap-y-4">
        <div className="grid grid-cols-row sm:pr-6 pr-0 gap-y-1">
          <h2 className="lg:text-xl text-lg font-bold line-clamp-1">{dealer.name}</h2>
          <div className="flex gap-x-2">
            <a href={dealer.website} target="_blank" className="text-black flex justify-center items-center size-7 rounded border">
              <img className="size-4" src={websiteIcon} alt="website" />
            </a>
            {dealer.socials.map((socialLink, i) => (
            <a href={socialLink.url} target="_blank" className="flex justify-center items-center size-7 rounded border" key={i}>
              <img className="size-4" src={socialIcons[socialLink.platform]} alt="facebook" />
            </a>
            ))}
          {/* {dealer.website
          ? <a href={dealer.website} target='_blank' className="text-sm text-inherit" onClick={event => event.stopPropagation()}>{dealer.website}</a>
          : <a>&nbsp;</a>} */}
          </div>
        </div>
        <div className="sm:pl-6 pl-0 flex gap-x-4 shrink-0">
          {dealer.awardBadges.map((award, i) => (
          <div key={i}>
            <a href={award.link} onClick={(e) => e.preventDefault()} target="_blank">
              <img className="lg:w-16 w-12" src={award.image.asset.url} alt={award.title} />
            </a>
          </div>
          ))}
        </div>
      </div>
      {priceListOptions.length > 0 &&
      <div className="flex gap-x-2">
          <Select className="z-[999]" style={{width: 128,}} options={priceListOptions} defaultValue={priceListOptions[0].value} onSelect={selectPriceList} onClick={event => event.stopPropagation()} />
          <Button type="primary" danger onClick={downloadPriceList}>Pricelist</Button>
      </div>}
    </div>
   )
  }

  const searchBarProps = {
    // onChange: (e) => {
    //   filterData(e.target.value);
    // },
    onSubmit: (value) => {
      navigate(`/new-cars/price-lists/?query=${value}`);
    },
    // searchValue: query,
  }
  
  return (
    <Layout mode={'fluid'} v3 showNewCarPriceListsSearchBar searchBarProps={searchBarProps}>
      <main className="max-w-[1440px] mx-auto px-4 md:px-8 xl:px-16 pt-[117px] lg:pt-[71px]">
        <BreadCrumb className="my-6 sm:my-8" items={breadCrumbItems} />
        <div className="text-content-primary bg-white lg:flex gap-x-8">
          <div className="w-full max-w-[976px] flex flex-col">
            <div className="make-profile md:mb-8 mb-4 md:p-8 p-4 bg-slate-50 rounded-lg">
              <div className="flex items-center gap-x-4 mb-8">
                <div className="shrink-0">
                  <img className="md:h-16 h-12" src={make.logo.asset.url} alt="" loading="lazy" />
                </div>
                <div className="w-full flex flex-col">
                  <h1 className="md:text-2xl text-lg font-bold leading-6">{make.name}</h1>
                  <div>
                    {make.website
                    ? <a className="md:text-base text-sm text-inherit" href={make.website} target="_blank">{make.website}</a>
                    : <a>&nbsp;</a>
                    }
                  </div>
                </div>
              </div>
              <Collapse className="drop-shadow" accordion defaultActiveKey="0" expandIconPosition="right" ghost>
                {make.dealers.map((d,i) => {
                const activePromotions = d.promotions.filter((p) => {
                  return moment().isBetween(p.startFrom, p.endDate);
                })

                return (
                <Panel className="z-0" header={<PanelHead dealer={d.dealer} priceLists={d.priceLists}/>} key={i}>
                  <div className="md:px-4 px-0 md:pb-4 pb-0 space-y-8">
                    { d.dealer.leaderboard && <img className="mt-8" src={urlFor(d.dealer.leaderboard).size(1940,500)} alt="" /> }
                    <Tabs defaultActiveKey="1">
                      <Tabs.TabPane tab="Showroom" key="1">
                          <h3 className="text-lg font-semibold mb-4">Showroom Locations</h3>
                          <div className="grid sm:grid-cols-2 grid-cols-1 sm:gap-8 gap-4">
                            {d.dealer.showrooms.map((outlet, i) => (
                              <div className="flex flex-col" key={i}>
                                <div className="text-base font-semibold">{outlet.name}</div>
                                <div className="flex gap-x-2">
                                  <div><EnvironmentOutlined /></div>
                                  <div>{outlet.address}
                                  {outlet.mapLink &&
                                  <a href={outlet.mapLink} target="_blank"> (Map)</a>}
                                  </div>
                                </div>
                                <div className="flex gap-x-2">
                                  <div><ClockCircleOutlined /></div>
                                  <div className="flex flex-col">
                                    <OpeningHours openingHours={outlet.openingHours}/>
                                  </div>
                                </div>
                                <div className="flex gap-x-2">
                                  <div className="rotate-90"><PhoneOutlined /></div>
                                  <div><a href={"tel://" + outlet.phoneNumber} className="text-inherit">{outlet.phoneNumber}</a></div>
                                </div>
                              </div>
                            ))}
                          </div>
                      </Tabs.TabPane>
                      <Tabs.TabPane tab="Service Center" key="2">
                          <h3 className="text-lg font-semibold mb-4">Service Center Locations</h3>
                          <div className="grid sm:grid-cols-2 grid-cols-1 sm:gap-8 gap-4">
                            {d.dealer.serviceCenters.map((outlet, i) => (
                              <div className="flex flex-col" key={i}>
                                <div className="text-base font-semibold">{outlet.name}</div>
                                <div className="flex gap-x-2">
                                  <div><EnvironmentOutlined /></div>
                                  <div>{outlet.address}
                                  {outlet.mapLink &&
                                  <a href={outlet.mapLink} target="_blank"> (Map)</a>}
                                  </div>
                                </div>
                                <div className="flex gap-x-2">
                                  <div><ClockCircleOutlined /></div>
                                  <div className="flex flex-col">
                                    <OpeningHours openingHours={outlet.openingHours}/>
                                  </div>
                                </div>
                                <div className="flex gap-x-2">
                                  <div className="rotate-90"><PhoneOutlined /></div>
                                  <div><a href={"tel://" + outlet.phoneNumber} className="text-inherit">{outlet.phoneNumber}</a></div>
                                </div>
                              </div>
                            ))}
                            </div>
                        </Tabs.TabPane>
                      </Tabs>
                      {activePromotions.length > 0 &&
                      <section>
                        <h3 className="text-xl font-bold mb-4">Current Promotions</h3>
                        <div className="grid md:grid-cols-3 sm:grid-cols-2 grid-col-1 gap-x-4 gap-y-3">
                          {activePromotions.map((promotion, i) => (
                            <a key={i} href={promotion.link} target="_blank" className="text-base text-inherit">
                              <div className="h-full bg-white rounded-lg shadow">
                                { promotion.thumbnail &&
                                  <img className="rounded-t" src={promotion.thumbnail.asset.url} alt={promotion.name} loading="lazy" />
                                }
                                <div className="grid p-4 gap-y-2">
                                  <div class="text-base font-extrabold line-clamp-2">{promotion.name}</div>
                                  {console.log(promotion)}
                                  <div class="text-xs leading-tight whitespace-pre-line">{promotion.excerpt}</div>
                                </div>
                              </div>
                            </a>
                          ))}
                        </div>
                      </section>
                      }
                      { d.dealer.featuredAwardWins.length > 0 &&
                        <section>
                          <h3 className="text-xl font-bold mb-4">{d.dealer.featuredAward.longTitle} Wins</h3>
                            <div className="grid md:grid-cols-3 sm:grid-cols-2 grid-col-1 gap-x-4 gap-y-3">
                              {d.dealer.featuredAwardWins.map((winner, i) => (
                                <COTY2024WinnerCard winner={winner} key={i}/>
                              ))}
                          </div>
                        </section>
                      }
                      <DealerRelatedMakes dealer={d.dealer} make={make} />
                    </div>
                  </Panel>
                )})}
              </Collapse>
            </div>

            <div className="grid grid-cols-1 sm:gap-y-8 gap-y-4">
              {reviews.length > 0 && 
              <div key="reviews">
                <div className="flex justify-between">
                  <h2 className="text-lg lg:text-2xl font-bold">Latest {make.name} Reviews</h2>
                    <a
                      href={`/articles/search/?category=car-reviews&make=${make.name}`}
                      className="w-[150px] justify-end text-base flex items-center gap-x-2.5 flex-shrink-0 font-medium text-os-blue"
                    >
                      Explore more
                      <img src={rightArrow} alt="right arrow" className="w-3" />
                    </a>
                </div>
                <div className="lg:mt-8 mt-4 flex flex-col gap-y-4 sm:gap-y-8">
                  {reviews.map((a) =>
                    <ArticleCard key={a._id} article={a} />
                  )}
                </div>
              </div>
              }

              {articles.length > 0 && 
              <div key="articles">
                <div className="flex justify-between">
                  <h2 className="text-lg lg:text-2xl font-bold">Latest {make.name} Articles</h2>
                    <a
                      href={`/articles/search/?category=all&make=${make.name}`}
                      className="w-[150px] justify-end text-base flex items-center gap-x-2.5 flex-shrink-0 font-medium text-os-blue"
                    >
                      Explore more
                      <img src={rightArrow} alt="right arrow" className="w-3" />
                    </a>
                </div>
                <div className="lg:mt-8 mt-4 flex flex-col gap-y-4 sm:gap-y-8">
                  {articles.map((a) =>
                    <ArticleCard key={a._id} article={a} />
                  )}
                </div>
              </div>
              }
            </div>

          </div>

          <section className="lg:w-[300px] flex lg:block flex-col justify-center items-center">
            <div className="sticky top-[200px]">
              <div className="mt-6">
              <Ads
                desktop={{
                  adsId:
                    '/325540922/oneshift_web/desktop_make_profile_rect_atf_01',
                  sizes: [[300, 250], 'fluid'],
                }}
                mobile={{
                  adsId:
                    '/325540922/oneshift_web/mobile_make_profile_rect_btf_01',
                  sizes: [[300, 250], 'fluid'],
                }}
                divId="div-gpt-ad-new-car-make-profile"
              />
              </div>
              <GetQuoteCard utmMedium={`make-profile-${slug}`} />
            </div>
          </section>
        </div>
      </main>
      <AdsOutOfPage
        desktop={{
          adsId: '/325540922/oneshift_web/desktop_make_profile_interstitial_01',
          sizes: [[1024, 768]],
        }}
        mobile={{
          adsId: '/325540922/oneshift_web/mobile_make_profile_interstitial_01',
          sizes: [[320, 480]],
        }}
        divId="make-profile-oop-ads"
      />
    </Layout>
  );
};

export const Head = ({ location, params, data, pageContext }) => {
  const { name, logo, website, dealers } = data.sanityMake;
  const title = `${name} Pricelist in Singapore | Showrooms, Reviews & Awards | OneShift by Carousell`;
  const description = `${name} in Singapore - Find official showrooms, service centers, latest pricelists, and expert reviews. Visit authorized dealers across Singapore for test drives and car purchases.`;
  const imageUrl = urlFor(logo).format('jpg').ignoreImageParams().size(1200,630).bg('fff').pad(100).fit('fill').url();
  
  const structuredDataWithoutUrl = (siteUrl) => {
    // Create organization data for the car manufacturer
    const organizationData = createOrganizationData({
      name,
      description,
      logo: logo.asset.url,
      url: website,
      sameAs: dealers[0]?.dealer?.socials?.map(social => social.url) || []
    });

    // Create local business data for showrooms and service centers
    const localBusinessData = dealers.flatMap(({ dealer }) => {
      const showrooms = dealer.showrooms.map(showroom => 
        createAutoDealerData({
          name: showroom.name,
          address: showroom.address,
          telephone: showroom.phoneNumber,
          openingHours: showroom.openingHours
        })
      );

      const serviceCenters = dealer.serviceCenters.map(center => 
        createAutoRepairData({
          name: center.name,
          address: center.address,
          telephone: center.phoneNumber,
          openingHours: center.openingHours
        })
      );

      return [...showrooms, ...serviceCenters];
    });

    // Create article list data for reviews and articles
    const reviews = data.reviews?.edges.map(e => e.node) || [];
    const articles = data.articles?.edges.map(e => e.node) || [];
    const articleListData = createArticleListData([...reviews, ...articles]);

    // Create breadcrumb data
    const breadcrumbData = createBreadcrumbData([
      { name: 'Home', path: '/' },
      { name: 'Buy', path: '/' },
      { name: 'New Car Pricelists', path: '/new-cars/price-lists' },
      { name: name, path: location.pathname }
    ], siteUrl);

    return [organizationData, ...localBusinessData, articleListData, breadcrumbData];
  };

  return <SEO 
    title={title} 
    description={description} 
    imageUrl={imageUrl} 
    pathname={location.pathname}
    getStructuredData={structuredDataWithoutUrl}
  />;
}

export const query = graphql`
  query ($makeId: String) {
    sanityMake(_id: {eq: $makeId}) {
      id
      _id
      name
      website
      logo {
        asset {
          id
          title
          description
          label
          url
        }
      }
      dealers {
        dealer {
          id
          _id
          name
          website
          leaderboard {
            asset {
              url
            }
          }
          socials {
            platform
            url
          }
          showrooms {
            name
            address
            phoneNumber
            mapLink
            openingHours {
              mon
              tue
              wed
              thu
              fri
              sat
              sun
              ph
            }
          }
          serviceCenters {
            name
            address
            phoneNumber
            mapLink
            openingHours {
              mon
              tue
              wed
              thu
              fri
              sat
              sun
              ph
            }
          }
          awards {
            title
            image {
              asset {
                url
              }
            }
          }
          cotyArticles {
            image {
              asset {
                url
              }
            }
            award
            title
            icon
            description
            Article {
              category_v3 {
                slug {
                  current
                }
              }
              title
              slug {
                current
              }
            }
          }
          awardBadges {
            title
            longTitle
            image {
              asset {
                url
              }
            }
            link
          }
          featuredAward {
            title
            longTitle
          }
          featuredAwardWins {
            title
            description
            category {
              title
              image {
                asset {
                  url
                }
              }
            }
            segment {
              title
              image {
                asset {
                  url
                }
              }
            }
            image {
              asset {
                url
              }
            }
            badge {
              asset {
                url
              }
            }
            article {
              category_v3 {
                slug {
                  current
                }
              }
              slug {
                current
              }
              excerpt
            }
          }
        }
        priceLists {
          datePublished
          file {
            asset {
              url
            }
          }
        }
        promotions {
          name
          link
          excerpt
          startFrom
          endDate
          thumbnail {
            asset {
              id
              title
              description
              url
            }
          } 
        }
      }
    }
    reviews: allSanityArticle(
      filter: { hidden: { eq: false },
      make: {_id: {eq: $makeId}},
      category_v3: {title: {eq:"Reviews"}},
      },
      sort: { publishDate: DESC }, limit: 3
    ) {
      edges {
        node {
          _updatedAt
          _id
          id
          title
          publishDate
          tags
          tags_v3 {
            name
            key {
              current
            }
          }
          category_v3 {
            _id
            id
            title
            slug {
              current
            }
          }
          slug {
            current
          }
          excerpt
          featured
          thumbnail {
            asset {
              id
              title
              description
              url
            }
          } 
        }
      }
    }
    articles: allSanityArticle(
      filter: { hidden: { eq: false },
      make: {_id: {eq: $makeId}},
      category_v3: {title: { ne: "Reviews"}},
      },
      sort: { publishDate: DESC }, limit: 3
    ) {
      edges {
        node {
          _updatedAt
          _id
          id
          title
          publishDate
          tags
          tags_v3 {
            name
            key {
              current
            }
          }
          category_v3 {
            _id
            id
            title
            slug {
              current
            }
          }
          slug {
            current
          }
          excerpt
          featured
          thumbnail {
            asset {
              id
              title
              description
              url
            }
          }
        }
      }
    }
  }`;

export default MakeProfile;